// ── Onboarding conversion mockup data ────────────────────────────────────────
// Powers /app-screen-mockups/onboarding-funnel.
//
// One flow: the Sign in click, every screen after it, and where people drop off,
// framed by the business numbers a SaaS operator checks first.
// Numbers are illustrative.
// ─────────────────────────────────────────────────────────────────────────────

export const fmtNum = (n: number) => n.toLocaleString('en-US');
/** Currency symbol held as a value so template literals never swallow it. */
export const USD = String.fromCharCode(36);
export const money = (n: number) => USD + n.toLocaleString('en-US');

// ── Reporting period ─────────────────────────────────────────────────────────
// Every number below is stated for a 30-day period, then scaled to whatever
// range is selected so the dashboard responds to the picker.

export interface RangeOption {
  label: string;
  /** Days in the period — used to scale the 30-day baseline. */
  days: number;
  /** How the period-over-period comparison is described. */
  comparison: string;
}
export const RANGES: RangeOption[] = [{
  label: 'Last 24 hours',
  days: 1,
  comparison: 'vs previous day'
}, {
  label: 'Last 7 days',
  days: 7,
  comparison: 'vs previous week'
}, {
  label: 'Last 30 days',
  days: 30,
  comparison: 'vs previous 30 days'
}, {
  label: 'Last 90 days',
  days: 90,
  comparison: 'vs previous quarter'
}, {
  label: 'Last 180 days',
  days: 180,
  comparison: 'vs previous 180 days'
}, {
  label: 'Last 365 days',
  days: 365,
  comparison: 'vs previous year'
}];
export const DEFAULT_RANGE = RANGES[2];

/** Rate of change shifts with the window: short windows are noisier. */
const deltaFor = (base: number, days: number) => days <= 1 ? Number((base * 2.4).toFixed(1)) : days <= 7 ? Number((base * 1.5).toFixed(1)) : days >= 180 ? Number((base * 0.6).toFixed(1)) : base;
export interface Headline {
  label: string;
  value: string;
  sub: string;
  delta: number;
  tone?: 'good' | 'bad';
}

/**
 * Business headline metrics for a period. Revenue leads because it is the
 * number being checked; MRR sits last because it is a run-rate, not a
 * period total, and does not scale with the window.
 */
export function headlinesFor(range: RangeOption): Headline[] {
  const f = range.days / 30;
  const revenue = Math.round(17_250 * f);
  const refunds = Math.round(1_150 * f);
  const subs = Math.max(1, Math.round(71 * f));
  const signups = Math.max(1, Math.round(1_690 * f));
  const netNew = Math.round(4_350 * f);
  const paidRate = (subs / signups * 100).toFixed(1);
  return [{
    label: 'Revenue',
    value: money(revenue),
    sub: 'collected, net of refunds',
    delta: deltaFor(12.4, range.days)
  }, {
    label: 'New subscriptions',
    value: fmtNum(subs),
    sub: money(259) + ' average plan',
    delta: deltaFor(9, range.days)
  }, {
    label: 'New signups',
    value: fmtNum(signups),
    sub: 'workspaces set up',
    delta: deltaFor(6.2, range.days)
  }, {
    label: 'Refunds',
    value: money(refunds),
    sub: `${Math.max(1, Math.round(5 * f))} refunded · 6.3% of gross`,
    delta: deltaFor(2.1, range.days),
    tone: 'bad'
  }, {
    label: 'Signup → paid',
    value: `${paidRate}%`,
    sub: `${fmtNum(subs)} of ${fmtNum(signups)} signups`,
    delta: deltaFor(0.4, range.days)
  }, {
    label: 'MRR',
    value: money(41_600),
    sub: `${money(netNew)} net new this period`,
    delta: deltaFor(11.7, range.days)
  }];
}

/** A single point in the flow, from the CTA click to a paid subscription. */
export interface FlowStep {
  id: string;
  label: string;
  definition: string;
  users: number;
  /** Median time from the previous point. */
  medianTime: string;
  section: 'Sign up' | 'Set up' | 'Use it' | 'Pay';
  event: string;
  tracked: boolean;
}
export const FLOW: FlowStep[] = [{
  id: 'cta',
  label: 'Clicked Sign Up',
  definition: 'Any Sign up or Get started button',
  users: 4_216,
  medianTime: '—',
  section: 'Sign up',
  event: 'Clicked Sign Up',
  tracked: true
}, {
  id: 'slack',
  label: 'Authorised Slack',
  definition: 'Slack OAuth returned successfully',
  users: 1_918,
  medianTime: '46s',
  section: 'Set up',
  event: 'signup_slack_connected',
  tracked: true
}, {
  id: 'channels',
  label: 'Chose channels',
  definition: 'Channel step submitted',
  users: 1_802,
  medianTime: '31s',
  section: 'Set up',
  event: 'signup_step_completed',
  tracked: false
}, {
  id: 'profile',
  label: 'Completed About you',
  definition: 'Role and use cases submitted',
  users: 1_690,
  medianTime: '54s',
  section: 'Set up',
  event: 'signup_profile_completed',
  tracked: true
}, {
  id: 'tools_picked',
  label: 'Picked tools',
  definition: 'Tool picker submitted',
  users: 1_544,
  medianTime: '1m 12s',
  section: 'Set up',
  event: 'signup_step_completed',
  tracked: false
}, {
  id: 'tool_connected',
  label: 'Connected a tool',
  definition: 'At least one integration authorised',
  users: 1_106,
  medianTime: '3m 05s',
  section: 'Set up',
  event: 'integration_connected',
  tracked: false
}, {
  id: 'first_chat',
  label: 'Asked a first question',
  definition: 'First message sent to Giga',
  users: 902,
  medianTime: '7m',
  section: 'Use it',
  event: 'first_chat_sent',
  tracked: false
}, {
  id: 'grounded',
  label: 'Got a grounded answer',
  definition: 'An answer that cited a connected tool',
  users: 764,
  medianTime: '40s',
  section: 'Use it',
  event: 'first_grounded_answer',
  tracked: false
}, {
  id: 'day2',
  label: 'Came back a second day',
  definition: 'Active on a later calendar day',
  users: 512,
  medianTime: '1d 4h',
  section: 'Use it',
  event: 'day2_return',
  tracked: false
}, {
  id: 'paywall',
  label: 'Spent the starter credit',
  definition: 'Starter credit reached zero',
  users: 181,
  medianTime: '17d',
  section: 'Pay',
  event: 'starter_credit_exhausted',
  tracked: false
}, {
  id: 'plan',
  label: 'Chose a plan',
  definition: 'Plan selected at the paywall',
  users: 96,
  medianTime: '3m',
  section: 'Pay',
  event: 'pricing_plan_selected',
  tracked: true
}, {
  id: 'paid',
  label: 'Subscribed',
  definition: 'Payment succeeded',
  users: 71,
  medianTime: '2m',
  section: 'Pay',
  event: 'subscription_started',
  tracked: false
}];

/** Which CTA was clicked, and how far those clicks get. */
export const CTA_SOURCES: {
  label: string;
  clicks: number;
  share: number;
  setupRate: number;
  paidRate: number;
  revenue: number;
}[] = [{
  label: 'Home hero — Get started',
  clicks: 2_192,
  share: 52,
  setupRate: 37,
  paidRate: 1.3,
  revenue: 6_800
}, {
  label: 'Nav — Sign in',
  clicks: 886,
  share: 21,
  setupRate: 42,
  paidRate: 1.9,
  revenue: 4_100
}, {
  label: 'Pricing page',
  clicks: 717,
  share: 17,
  setupRate: 51,
  paidRate: 2.4,
  revenue: 6_450
}, {
  label: 'Demo / other',
  clicks: 421,
  share: 10,
  setupRate: 33,
  paidRate: 0.9,
  revenue: 1_050
}];

/** The flow scaled to a period, so the funnel moves with the picker. */
export function flowFor(range: RangeOption): FlowStep[] {
  const f = range.days / 30;
  return FLOW.map(step => ({
    ...step,
    users: Math.max(1, Math.round(step.users * f))
  }));
}

/** Cohorts by signup week. */
export const COHORTS: {
  week: string;
  clicks: number;
  setup: number;
  used: number;
  paid: number;
  revenue: number;
  ripe: boolean;
}[] = [{
  week: 'Jun 8',
  clicks: 612,
  setup: 402,
  used: 288,
  paid: 31,
  revenue: 7_900,
  ripe: true
}, {
  week: 'Jun 15',
  clicks: 688,
  setup: 441,
  used: 301,
  paid: 24,
  revenue: 6_200,
  ripe: true
}, {
  week: 'Jun 22',
  clicks: 731,
  setup: 502,
  used: 355,
  paid: 16,
  revenue: 4_300,
  ripe: true
}, {
  week: 'Jun 29',
  clicks: 802,
  setup: 559,
  used: 402,
  paid: 0,
  revenue: 0,
  ripe: false
}, {
  week: 'Jul 6',
  clicks: 774,
  setup: 528,
  used: 361,
  paid: 0,
  revenue: 0,
  ripe: false
}, {
  week: 'Jul 13',
  clicks: 373,
  setup: 246,
  used: 148,
  paid: 0,
  revenue: 0,
  ripe: false
}];

/** Only the weeks that fall inside the selected period. */
export function cohortsFor(range: RangeOption) {
  const weeks = Math.max(1, Math.min(COHORTS.length, Math.round(range.days / 7)));
  return COHORTS.slice(-weeks);
}

/** The events behind the flow. */
export const EVENTS: {
  event: string;
  fires: string;
  properties: string[];
  tracked: boolean;
}[] = [{
  event: 'Clicked Sign Up',
  fires: 'Any Sign up / Get started button',
  properties: ['cta_location', 'utm_source'],
  tracked: true
}, {
  event: 'signup_slack_connected',
  fires: 'Slack OAuth returns successfully',
  properties: ['workspace_id', 'was_admin'],
  tracked: true
}, {
  event: 'signup_step_completed',
  fires: 'Each onboarding screen is submitted',
  properties: ['step_name', 'seconds_on_step'],
  tracked: false
}, {
  event: 'signup_profile_completed',
  fires: 'About you is submitted',
  properties: ['role', 'use_cases'],
  tracked: true
}, {
  event: 'integration_connected',
  fires: 'A tool finishes authorising',
  properties: ['tool_slug', 'error_code'],
  tracked: false
}, {
  event: 'first_chat_sent',
  fires: 'First message to Giga',
  properties: ['surface'],
  tracked: false
}, {
  event: 'first_grounded_answer',
  fires: 'An answer cites a connected tool',
  properties: ['tools_used'],
  tracked: false
}, {
  event: 'day2_return',
  fires: 'Active on a later calendar day',
  properties: ['days_since_signup'],
  tracked: false
}, {
  event: 'credits_consumed',
  fires: 'Any credit-spending action',
  properties: ['credits', 'feature', 'running_total'],
  tracked: false
}, {
  event: 'starter_credit_exhausted',
  fires: 'Starter credit reaches zero',
  properties: ['days_since_signup'],
  tracked: false
}, {
  event: 'pricing_plan_selected',
  fires: 'Plan chosen at the paywall',
  properties: ['credits', 'price'],
  tracked: true
}, {
  event: 'subscription_started',
  fires: 'Payment succeeds',
  properties: ['price', 'credits'],
  tracked: false
}, {
  event: 'refund_issued',
  fires: 'A payment is refunded',
  properties: ['amount', 'reason', 'days_since_charge'],
  tracked: false
}];